import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
	PlusIcon,
	PencilIcon,
	TrashIcon,
	ArrowUpIcon,
	ArrowDownIcon,
	EyeIcon,
	EyeSlashIcon
} from '@heroicons/react/24/outline';

import { API_URL } from '../../config/api';

const ProjectStatisticManagement = () => {
	const [statistics, setStatistics] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState('');
	const [showModal, setShowModal] = useState(false);
	const [editingStatistic, setEditingStatistic] = useState(null);
	const [formData, setFormData] = useState({
		category: 'constitucion',
		title: '',
		value: 0,
		icon: '',
		suffix: '',
		active: true,
		order: 0
	});
	const [formErrors, setFormErrors] = useState({});
	const [saving, setSaving] = useState(false);

	useEffect(() => {
		loadStatistics();
	}, []);

	const loadStatistics = async () => {
		try {
			setLoading(true);
			const response = await axios.get(`${API_URL}/api/content/project-statistics/`);
			setStatistics(Array.isArray(response.data) ? response.data : (response.data.results || []));
			setError('');
		} catch (err) {
			setError('Error al cargar estadísticas');
			console.error('Error cargando estadísticas:', err);
		} finally {
			setLoading(false);
		}
	};

	const handleCreate = () => {
		setEditingStatistic(null);
		setFormData({
			category: 'constitucion',
			title: '',
			value: 0,
			icon: '',
			suffix: '',
			active: true,
			order: statistics.length
		});
		setFormErrors({});
		setShowModal(true);
	};

	const handleEdit = (statistic) => {
		setEditingStatistic(statistic);
		setFormData({
			category: statistic.category || 'constitucion',
			title: statistic.title || '',
			value: statistic.value || 0,
			icon: statistic.icon || '',
			suffix: statistic.suffix || '',
			active: statistic.active !== undefined ? statistic.active : true,
			order: statistic.order || 0
		});
		setFormErrors({});
		setShowModal(true);
	};

	const handleSave = async (e) => {
		e.preventDefault();
		setFormErrors({});
		setSaving(true);

		try {
			if (editingStatistic) {
				await axios.put(`${API_URL}/api/content/project-statistics/${editingStatistic.id}/`, formData);
			} else {
				await axios.post(`${API_URL}/api/content/project-statistics/`, formData);
			}

			await loadStatistics();
			setShowModal(false);
		} catch (err) {
			if (err.response?.data) {
				setFormErrors(err.response.data);
			} else {
				setError('Error al guardar estadística');
			}
		} finally {
			setSaving(false);
		}
	};

	const handleDelete = async (id) => {
		if (!window.confirm('¿Estás seguro de eliminar esta estadística?')) return;

		try {
			await axios.delete(`${API_URL}/api/content/project-statistics/${id}/`);
			await loadStatistics();
		} catch (err) {
			setError('Error al eliminar estadística');
		}
	};

	const handleToggleActive = async (statistic) => {
		try {
			await axios.patch(`${API_URL}/api/content/project-statistics/${statistic.id}/`, {
				active: !statistic.active
			});
			await loadStatistics();
		} catch (err) {
			setError('Error al cambiar estado de la estadística');
		}
	};

	const getCategoryLabel = (category) => {
		switch (category) {
			case 'constitucion': return 'Constitución de Empresas & ONG';
			case 'outsourcing': return 'Outsourcing Contable y Fiscal';
			case 'otros': return 'Otros Servicios';
			default: return category;
		}
	};

	return (
		<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
			{/* Header */}
			<div className="mb-8">
				<div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
					<div>
						<h1 className="text-3xl font-bold text-gray-900">Gestión de Estadísticas</h1>
						<p className="mt-2 text-sm text-gray-600">
							Administra las estadísticas del proyecto
						</p>
					</div>
					<button
						onClick={handleCreate}
						className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-white transition-opacity"
						style={{ backgroundColor: '#12284C' }}
					>
						<PlusIcon className="h-5 w-5" />
						Crear Estadística
					</button>
				</div>
			</div>

			{/* Error */}
			{error && (
				<div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
					<p className="text-red-800">{error}</p>
				</div>
			)}

			{/* Lista de Estadísticas */}
			{loading ? (
				<div className="text-center py-12">
					<div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto" style={{ borderColor: '#12284C' }}></div>
					<p className="mt-4 text-gray-600">Cargando estadísticas...</p>
				</div>
			) : (
				<div className="bg-white rounded-lg shadow overflow-hidden">
					<div className="overflow-x-auto">
						<table className="min-w-full divide-y divide-gray-200">
							<thead className="bg-gray-50">
								<tr>
									<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
										Categoría
									</th>
									<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
										Título
									</th>
									<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
										Valor
									</th>
									<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
										Estado
									</th>
									<th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
										Acciones
									</th>
								</tr>
							</thead>
							<tbody className="bg-white divide-y divide-gray-200">
								{statistics.length === 0 ? (
									<tr>
										<td colSpan="5" className="px-6 py-8 text-center text-gray-500">
											No hay estadísticas creadas
										</td>
									</tr>
								) : (
									statistics.map((statistic) => (
										<tr key={statistic.id} className="hover:bg-gray-50">
											<td className="px-6 py-4 whitespace-nowrap">
												<span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
													{getCategoryLabel(statistic.category)}
												</span>
											</td>
											<td className="px-6 py-4">
												<div className="text-sm font-medium text-gray-900">{statistic.title}</div>
											</td>
											<td className="px-6 py-4 whitespace-nowrap">
												<div className="text-sm text-gray-900">
													{statistic.value}{statistic.suffix}
												</div>
											</td>
											<td className="px-6 py-4 whitespace-nowrap">
												{statistic.active ? (
													<span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
														Activo
													</span>
												) : (
													<span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
														Inactivo
													</span>
												)}
											</td>
											<td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
												<div className="flex items-center justify-end gap-2">
													<button
														onClick={() => handleToggleActive(statistic)}
														className="p-2 hover:bg-gray-100 rounded"
														title={statistic.active ? 'Desactivar' : 'Activar'}
													>
														{statistic.active ? (
															<EyeSlashIcon className="h-5 w-5 text-gray-600" />
														) : (
															<EyeIcon className="h-5 w-5 text-gray-600" />
														)}
													</button>
													<button
														onClick={() => handleEdit(statistic)}
														className="p-2 hover:bg-gray-100 rounded"
														title="Editar"
													>
														<PencilIcon className="h-5 w-5" style={{ color: '#12284C' }} />
													</button>
													<button
														onClick={() => handleDelete(statistic.id)}
														className="p-2 hover:bg-red-50 rounded"
														title="Eliminar"
													>
														<TrashIcon className="h-5 w-5 text-red-600" />
													</button>
												</div>
											</td>
										</tr>
									))
								)}
							</tbody>
						</table>
					</div>
				</div>
			)}

			{/* Modal Crear/Editar */}
			{showModal && (
				<div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
					<div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
						<div className="p-6">
							<div className="flex items-center justify-between mb-6">
								<h2 className="text-2xl font-bold text-gray-900">
									{editingStatistic ? 'Editar Estadística' : 'Crear Estadística'}
								</h2>
								<button
									onClick={() => setShowModal(false)}
									className="text-gray-400 hover:text-gray-600"
								>
									<svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
										<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
									</svg>
								</button>
							</div>

							<form onSubmit={handleSave} className="space-y-4">
								<div>
									<label className="block text-sm font-medium text-gray-700 mb-1">
										Categoría *
									</label>
									<select
										required
										value={formData.category}
										onChange={(e) => setFormData({ ...formData, category: e.target.value })}
										className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-offset-0"
										style={{ focusRingColor: '#12284C' }}
									>
										<option value="constitucion">Constitución de Empresas & ONG</option>
										<option value="outsourcing">Outsourcing Contable y Fiscal</option>
										<option value="otros">Otros Servicios</option>
									</select>
								</div>

								<div>
									<label className="block text-sm font-medium text-gray-700 mb-1">
										Título *
									</label>
									<input
										type="text"
										required
										value={formData.title}
										onChange={(e) => setFormData({ ...formData, title: e.target.value })}
										className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-offset-0"
										style={{ focusRingColor: '#12284C' }}
										placeholder="Ej: Empresas constituidas"
									/>
								</div>

								<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
									<div>
										<label className="block text-sm font-medium text-gray-700 mb-1">
											Valor *
										</label>
										<input
											type="number"
											required
											value={formData.value}
											onChange={(e) => setFormData({ ...formData, value: parseInt(e.target.value) || 0 })}
											className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-offset-0"
											style={{ focusRingColor: '#12284C' }}
										/>
									</div>
									<div>
										<label className="block text-sm font-medium text-gray-700 mb-1">
											Sufijo
										</label>
										<input
											type="text"
											value={formData.suffix}
											onChange={(e) => setFormData({ ...formData, suffix: e.target.value })}
											className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-offset-0"
											style={{ focusRingColor: '#12284C' }}
											placeholder="Ej: +, %, empresas"
										/>
									</div>
								</div>

								<div>
									<label className="block text-sm font-medium text-gray-700 mb-1">
										Icono (opcional)
									</label>
									<input
										type="text"
										value={formData.icon}
										onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
										className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-offset-0"
										style={{ focusRingColor: '#12284C' }}
										placeholder="Nombre del icono"
									/>
								</div>

								<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
									<div>
										<label className="block text-sm font-medium text-gray-700 mb-1">
											Orden
										</label>
										<input
											type="number"
											value={formData.order}
											onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
											className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-offset-0"
											style={{ focusRingColor: '#12284C' }}
										/>
									</div>
									<div>
										<label className="flex items-center space-x-2">
											<input
												type="checkbox"
												checked={formData.active}
												onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
												className="rounded border-gray-300"
												style={{ accentColor: '#12284C' }}
											/>
											<span className="text-sm font-medium text-gray-700">Activo</span>
										</label>
									</div>
								</div>

								{Object.keys(formErrors).length > 0 && (
									<div className="bg-red-50 border border-red-200 rounded-lg p-4">
										{Object.entries(formErrors).map(([key, value]) => (
											<p key={key} className="text-sm text-red-800">
												{key}: {Array.isArray(value) ? value.join(', ') : value}
											</p>
										))}
									</div>
								)}

								<div className="flex justify-end gap-3 pt-4 border-t">
									<button
										type="button"
										onClick={() => setShowModal(false)}
										className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
									>
										Cancelar
									</button>
									<button
										type="submit"
										disabled={saving}
										className="px-4 py-2 rounded-lg text-white transition-opacity disabled:opacity-50"
										style={{ backgroundColor: '#12284C' }}
									>
										{saving ? 'Guardando...' : 'Guardar'}
									</button>
								</div>
							</form>
						</div>
					</div>
				</div>
			)}
		</div>
	);
};

export default ProjectStatisticManagement;

